"use client"
import React, { useState } from 'react'
import axios from 'axios'
import { motion, AnimatePresence } from "motion/react"
import { BlurFade } from '@/app/components/BlurFade'
import { MMHeader, MMTabBar, MMFooter } from '../../components/MMChrome'
import { RedirectToPC } from '@/app/components/DeviceRedirect'
import { pick, type Locale } from '@/lib/i18n'
import { mmDict } from '@/lib/i18n/dicts/mm'

// 服务承诺图标（与文案 promises 按顺序对应）
const PROMISE_ICONS = [
    'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
    'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
    'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066 2.573c.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
]

const inputCls = (invalid: boolean) =>
    `w-full rounded-xl bg-slate-950/60 ring-1 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-all duration-300 focus:ring-2 focus:bg-slate-950 ${invalid
        ? 'ring-red-500 focus:ring-red-400'
        : 'ring-white/10 focus:ring-indigo-400'
    }`

export default function MMContactClient({ locale }: { locale: Locale }) {
    const [UserName, setUserName] = useState('')
    const [TelNumber, setTelNumber] = useState('')
    const [Company, setCompany] = useState('')
    const [Email, setEmail] = useState('')
    const [invalid, setInvalid] = useState({ name: false, tel: false })
    const [submitting, setSubmitting] = useState(false)
    const [openFaq, setOpenFaq] = useState(-1)
    const t = pick(mmDict, locale).contact

    async function handleSubmit() {
        const nameEmpty = UserName.trim() === ''
        const telEmpty = TelNumber.trim() === ''
        if (nameEmpty || telEmpty) {
            setInvalid({ name: nameEmpty, tel: telEmpty })
            return
        }
        setInvalid({ name: false, tel: false })
        setSubmitting(true)

        const reqBody = { Source: 'Web-Mobile', Name: UserName, Phone: TelNumber, Email: Email, Company: Company }

        try {
            const response = await axios({
                method: 'post',
                url: '/api/sdk/ywq/AddContact',
                timeout: 5000,
                data: reqBody,
            })
            if (response.data.success == true) {
                alert(t.form.success)
                setUserName(''); setTelNumber(''); setCompany(''); setEmail('')
            } else {
                alert(t.form.fail)
            }
        } catch (error) {
            // 与原页保持一致：网络异常静默
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div className="bg-slate-950 min-h-screen text-slate-200 max-w-md mx-auto relative shadow-2xl shadow-black">
            <MMHeader locale={locale} />
            <RedirectToPC href="/contact2" />

            {/* Hero */}
            <section className="relative px-5 pt-8 pb-6 overflow-hidden">
                <div className="pointer-events-none absolute -top-24 left-1/2 h-56 w-56 -translate-x-1/2 rounded-full bg-indigo-600/20 blur-[90px]" />
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-500/10 ring-1 ring-indigo-400/30 px-3 py-1 text-[11px] font-medium text-indigo-300">
                        <span className="relative flex size-1.5">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-indigo-400 opacity-75"></span>
                            <span className="relative inline-flex size-1.5 rounded-full bg-indigo-400"></span>
                        </span>
                        {t.badge}
                    </span>
                    <h1 className="mt-4 text-3xl font-black text-white">
                        {t.titlePre}<span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-300 to-fuchsia-300">{t.titleAccent}</span>
                    </h1>
                    <p className="mt-2 text-xs leading-5 text-slate-400">
                        {t.desc}
                    </p>
                </motion.div>
            </section>

            {/* 表单 */}
            <section className="px-4 pb-6">
                <BlurFade delay={0.1} inView>
                    <div className="rounded-2xl bg-slate-900 ring-1 ring-white/10 p-5">
                        <h2 className="text-base font-bold text-white">{t.form.title}</h2>
                        <p className="mt-1 text-[11px] text-slate-500">{t.form.requiredPre}<span className="text-red-400">*</span>{t.form.requiredPost}</p>

                        <div className="mt-5 flex flex-col gap-4">
                            <div className="flex flex-col gap-1.5">
                                <label className="text-xs font-medium text-slate-300">{t.form.name} <span className="text-red-400">*</span></label>
                                <input
                                    className={inputCls(invalid.name)}
                                    type="text"
                                    value={UserName}
                                    onChange={(e) => { setUserName(e.target.value); setInvalid((v) => ({ ...v, name: false })) }}
                                />
                                {invalid.name && <span className="text-[11px] text-red-400">{t.form.nameRequired}</span>}
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-xs font-medium text-slate-300">{t.form.tel} <span className="text-red-400">*</span></label>
                                <input
                                    className={inputCls(invalid.tel)}
                                    type="tel"
                                    value={TelNumber}
                                    onChange={(e) => { setTelNumber(e.target.value); setInvalid((v) => ({ ...v, tel: false })) }}
                                />
                                {invalid.tel && <span className="text-[11px] text-red-400">{t.form.telRequired}</span>}
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-xs font-medium text-slate-300">{t.form.email}</label>
                                <input className={inputCls(false)} type="email" value={Email} onChange={(e) => setEmail(e.target.value)} />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-xs font-medium text-slate-300">{t.form.company}</label>
                                <input className={inputCls(false)} type="text" value={Company} onChange={(e) => setCompany(e.target.value)} />
                            </div>
                        </div>

                        <button
                            onClick={handleSubmit}
                            disabled={submitting}
                            className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-indigo-500 to-fuchsia-500 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-indigo-600/30 active:scale-95 transition-all disabled:opacity-60"
                        >
                            {submitting ? t.form.submitting : t.form.submit}
                        </button>
                    </div>
                </BlurFade>
            </section>

            {/* 直接联系 */}
            <section className="px-4 pb-6">
                <BlurFade delay={0.15} inView>
                    <div className="rounded-2xl bg-gradient-to-b from-indigo-600/15 to-slate-900 ring-1 ring-indigo-400/20 p-5">
                        <h2 className="text-sm font-bold text-white">{t.direct.title}</h2>
                        <div className="mt-4 space-y-4">
                            <a href="tel:+8618800382271" className="flex items-center gap-3">
                                <span className="rounded-xl bg-indigo-500/10 ring-1 ring-indigo-400/30 p-2.5 text-indigo-400">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
                                    </svg>
                                </span>
                                <span>
                                    <span className="block text-[11px] text-slate-500">{t.direct.phoneLabel}</span>
                                    <span className="block mt-0.5 text-sm font-bold text-slate-100">（+86）188 0038 2271</span>
                                </span>
                            </a>
                            <a href="mailto:vip@yygame.tech" className="flex items-center gap-3">
                                <span className="rounded-xl bg-indigo-500/10 ring-1 ring-indigo-400/30 p-2.5 text-indigo-400">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                                    </svg>
                                </span>
                                <span>
                                    <span className="block text-[11px] text-slate-500">{t.direct.emailLabel}</span>
                                    <span className="block mt-0.5 text-sm font-bold text-slate-100">vip@yygame.tech</span>
                                </span>
                            </a>
                        </div>
                        <div className="mt-5 flex flex-col items-center rounded-xl bg-slate-950/50 ring-1 ring-white/5 p-5">
                            <p className="text-[11px] text-slate-400 mb-3">{t.direct.wechat}</p>
                            <img src="/img/wechat.png" alt={t.direct.wechatAlt} className="w-32 h-32 rounded-lg ring-1 ring-white/10" />
                        </div>
                    </div>
                </BlurFade>
            </section>

            {/* 服务承诺 */}
            <section className="px-4 pb-6">
                <div className="flex flex-col gap-3">
                    {t.promises.map((p, i) => (
                        <BlurFade key={p.title} delay={0.08 * i} inView>
                            <div className="flex items-start gap-3 rounded-2xl bg-slate-900 ring-1 ring-white/10 p-4">
                                <span className="rounded-xl bg-indigo-500/10 ring-1 ring-indigo-400/30 p-2.5 text-indigo-400 shrink-0">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
                                        <path strokeLinecap="round" strokeLinejoin="round" d={PROMISE_ICONS[i]} />
                                    </svg>
                                </span>
                                <div>
                                    <h3 className="text-sm font-bold text-white">{p.title}</h3>
                                    <p className="mt-1 text-[11px] leading-5 text-slate-400">{p.desc}</p>
                                </div>
                            </div>
                        </BlurFade>
                    ))}
                </div>
            </section>

            {/* 常见问题 */}
            <section className="px-4 pb-6">
                <BlurFade inView>
                    <h2 className="text-center text-xl font-black text-white">{t.faq.title}</h2>
                    <p className="mt-2 text-center text-xs text-slate-400">{t.faq.desc}</p>
                </BlurFade>
                <div className="mt-5 space-y-2.5">
                    {t.faq.items.map((faq, i) => {
                        const open = openFaq === i
                        return (
                            <BlurFade key={faq.q} delay={0.06 * i} inView>
                                <div className={`rounded-xl ring-1 overflow-hidden transition-colors duration-300 ${open ? 'bg-slate-900 ring-indigo-400/40' : 'bg-slate-900/60 ring-white/10'}`}>
                                    <button
                                        onClick={() => setOpenFaq(open ? -1 : i)}
                                        className="w-full flex items-center justify-between px-4 py-3.5 text-left"
                                    >
                                        <span className="text-xs font-medium text-slate-100 pr-3">{faq.q}</span>
                                        <motion.svg
                                            xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"
                                            className="h-4 w-4 shrink-0 text-indigo-400"
                                            animate={{ rotate: open ? 180 : 0 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                                        </motion.svg>
                                    </button>
                                    <AnimatePresence initial={false}>
                                        {open && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: 'auto', opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.3, ease: 'easeInOut' }}
                                            >
                                                <p className="px-4 pb-4 text-[11px] leading-5 text-slate-400">{faq.a}</p>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            </BlurFade>
                        )
                    })}
                </div>
            </section>

            <MMFooter locale={locale} />
            <MMTabBar active="contact" locale={locale} />
        </div>
    )
}
